import React, { useEffect, useState, useRef, useCallback } from "react";
import styled, { css } from "styled-components";
import {
    MdSearch, MdHome, MdBook, MdSchool,
    MdMessage, MdNotifications, MdLogout,
    MdClose, MdDone, MdClear, MdMenu,
    MdThumbUp, MdComment 
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
    doc, getDoc, updateDoc, deleteDoc, arrayUnion,
    collection, query, where, getDocs, limit,
    onSnapshot, orderBy, addDoc, serverTimestamp,
    or, and, setDoc 
} from "firebase/firestore";
import logo from "./logo.png";


const PrimaryColor = "#1a73e8";
const LightText = "#5f6368";
const BorderColor = "#dadce0";
const AccentColor = "#28a745";
const DangerColor = "#dc3545";


// --- Responsive Breakpoints ---

const HeaderContainer = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 50px 8px 25px;
    background-color: white;
    border-bottom: 1px solid ${BorderColor};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    height: 60px;
    position: sticky;
    top: 0;
    z-index: 1000;

    @media (max-width: 1440px) { padding: 8px 30px; }
    @media (max-width: 1024px) { padding: 8px 15px; }
    @media (max-width: 768px) { height: 50px; padding: 5px 10px; }
`;

const Logo = styled.div`
    display: flex; align-items: center; font-size: 22px; font-weight: 700; color: ${PrimaryColor}; cursor: pointer;
    img { height: 55px; margin-right: 13px; } margin-right: 0px; flex-shrink: 0;
    @media (max-width: 1024px) { margin-right: 20px; }
    @media (max-width: 768px) { font-size: 18px; margin-right: 10px; img { height: 25px; } order: 2; ${props => props.isSearchOpen && css` display: none; `} }
`;

const SearchBar = styled.div`
    width: 50%; max-width: 100px; position: relative; flex-shrink: 1; min-width: 250px;
    display: ${props => props.isSearchOpen ? 'block' : 'none'};
    @media (min-width: 769px) { display: block; }
    @media (max-width: 768px) { order: 3; position: fixed; top: 50px; left: 0; width: 100%; max-width: none; min-width: none; height: ${props => props.isSearchOpen ? '50px' : '0'}; background-color: white; z-index: 1000; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); display: block; transition: height 0.2s ease-in-out; overflow: hidden; ${props => !props.isSearchOpen && css` display: none; `} }
    ${props => !props.isSearchOpen && css` @media (min-width: 769px) { max-width: 0; min-width: 0; margin-right: -25px; visibility: hidden; } `}
`;

const SearchInputContainer = styled.div` position: relative; display: flex; align-items: center; width: 100%; @media (max-width: 768px) { padding: 5px 10px; } `;
const SearchInput = styled.input` width: 100%; padding: 10px 15px 10px 45px; border: 1px solid ${BorderColor}; border-radius: 30px; background-color: #f1f3f4; font-size: 16px; transition: 0.3s; &:focus { background-color: white; border-color: ${PrimaryColor}; outline: none; box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3); } @media (max-width: 768px) { padding: 8px 10px 8px 35px; font-size: 14px; border-radius: 20px; } `;
const SearchIcon = styled(MdSearch)` position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: ${LightText}; font-size: 22px; @media (max-width: 768px) { left: 10px; font-size: 18px; } `;
const SearchCloseIcon = styled(MdClose)` position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: ${LightText}; font-size: 24px; cursor: pointer; transition: color 0.2s; &:hover { color: ${DangerColor}; } @media (min-width: 769px) { display: none; } @media (max-width: 768px) { right: 15px; font-size: 20px; } `;
const SearchResultsDropdown = styled.div` position: absolute; top: 100%; left: 0; width: 100%; max-height: 300px; overflow-y: auto; background-color: white; border: 1px solid ${BorderColor}; border-top: none; border-radius: 0 0 8px 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); z-index: 2000; @media (max-width: 768px) { max-height: calc(100vh - 100px); border-radius: 0 0 8px 8px; top: 0; border-top: 1px solid ${BorderColor}; } `;
const SearchResultItem = styled.div` display: flex; align-items: center; padding: 10px 15px; cursor: pointer; border-bottom: 1px solid #f1f3f4; &:last-child { border-bottom: none; } &:hover { background-color: #f7f7f7; } `;
const ProfileImageSmall = styled.img` width: 30px; height: 30px; border-radius: 50%; object-fit: cover; margin-right: 10px; `;
const ResultDetails = styled.div` span { display: block; font-size: 14px; font-weight: 500; color: #3c4043; } small { font-size: 12px; color: ${LightText}; } `;
const NavLinks = styled.nav` display: flex; align-items: center; gap: 25px; margin-left: auto; flex-shrink: 0; @media (max-width: 1440px) { gap: 20px; } @media (max-width: 1024px) { gap: 15px; } @media (max-width: 768px) { order: 4; gap: 5px; ${props => props.isSearchOpen && css` display: none; `} } `;
const NavItem = styled.div` display: flex; align-items: center; gap: 5px; color: #1a73e8; font-size: 17px; font-weight: 700; cursor: pointer; transition: color 0.2s; &:hover { color: ${PrimaryColor}; } svg { font-size: 20px; } @media (max-width: 1024px) { span { display: none; } svg { font-size: 22px; } padding: 5px; border-radius: 50%; &:hover { background-color: #f1f3f4; color: ${PrimaryColor}; } } @media (max-width: 768px) { display: none; } `;
const MenuButton = styled.div` display: none; @media (max-width: 768px) { display: flex; align-items: center; justify-content: center; order: 1; margin-right: 10px; width: 30px; height: 30px; border-radius: 50%; color: ${LightText}; cursor: pointer; transition: 0.2s; &:hover { background-color: #f1f3f4; color: ${PrimaryColor}; } svg { font-size: 22px; } ${props => props.isSearchOpen && css` display: none; `} } `;
const MobileMenu = styled.div` display: none; @media (max-width: 768px) { display: block; position: fixed; top: 50px; left: 0; width: 220px; height: calc(100% - 50px); background-color: white; box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1); z-index: 1500; transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')}); transition: transform 0.3s ease-in-out; padding: 10px 0; overflow-y: auto; } `;
const MobileMenuItem = styled.div` display: flex; align-items: center; padding: 10px 15px; font-size: 17px; font-weight: 700; color: #3c4043; cursor: pointer; border-left: 3px solid transparent; transition: background-color 0.2s; position: relative; &:hover { background-color: #f1f3f4; border-left: 3px solid ${PrimaryColor}; } svg { margin-right: 10px; font-size: 20px; color: ${PrimaryColor}; } `;
const NotificationContainer = styled.div` position: relative; align-items: center; @media (max-width: 768px) { display: none; } `;
// Add a new styled component for the Message Icon Container
const MessageContainer = styled.div` position: relative; align-items: center; display: flex; `;
const IconButton = styled.div` width: 35px; height: 35px; border-radius: 50%; background-color: #f1f3f4; display: flex; align-items: center; justify-content: center; color: ${LightText}; cursor: pointer; transition: 0.2s; position: relative; &:hover { background-color: ${PrimaryColor}; color: white; } @media (max-width: 768px) { width: 30px; height: 30px; svg { font-size: 18px; } } `;
const SearchIconButton = styled(IconButton)` display: ${props => props.isSearchOpen ? 'none' : 'flex'}; @media (max-width: 768px) { order: 4; display: flex; margin-right: 5px; ${props => props.isSearchOpen && css` display: none; `} } `;
const MobileHiddenIconButton = styled(IconButton)` @media (max-width: 768px) { display: none; } `;
const NotificationBadge = styled.div` position: absolute; top: -3px; right: -3px; background-color: ${DangerColor}; color: white; border-radius: 50%; width: 18px; height: 18px; font-size: 10px; display: flex; align-items: center; justify-content: center; font-weight: 700; z-index: 1001; @media (max-width: 768px) { width: 15px; height: 15px; font-size: 8px; top: -2px; right: -2px; } `;
const ProfileIcon = styled.div` width: 35px; height: 35px; border-radius: 50%; overflow: hidden; background-color: ${PrimaryColor}; display: flex; justify-content: center; align-items: center; color: white; font-weight: 600; font-size: 14px; cursor: pointer; margin-left: 5px; box-shadow: 0 0 0 2px white, 0 0 0 3px ${PrimaryColor}; @media (max-width: 768px) { width: 30px; height: 30px; font-size: 12px; margin-left: 0; box-shadow: 0 0 0 1px white, 0 0 0 2px ${PrimaryColor}; ${props => props.isSearchOpen && css` display: none; `} } img { width: 100%; height: 100%; object-fit: cover; } `;

// Notification Components
const NotificationPopup = styled.div` position: absolute; top: 50px; right: 0; width: 350px; max-height: 400px; overflow-y: auto; background-color: white; border: 1px solid ${BorderColor}; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); z-index: 3000; padding: 10px 0; @media (max-width: 768px) { position: fixed; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 0; box-shadow: none; background-color: white; z-index: 5000; padding-top: 50px; } `;
const PopupHeader = styled.div` display: flex; justify-content: space-between; align-items: center; padding: 0 15px 10px; font-size: 16px; font-weight: 600; color: #3c4043; border-bottom: 1px solid ${BorderColor}; margin-bottom: 10px; @media (max-width: 768px) { font-size: 18px; padding: 10px 15px; position: fixed; top: 0; left: 0; width: 100%; background-color: white; z-index: 5001; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); } `;
const NotificationList = styled.div` display: flex; flex-direction: column; @media (max-width: 768px) { padding-top: 10px; } `;
const NotificationItem = styled.div` padding: 10px 15px; border-bottom: 1px solid #f1f3f4; cursor: pointer; background-color: ${(props) => (props.isRead ? 'white' : '#e8f0fe')}; transition: background-color 0.2s; &:hover { background-color: #f7f7f7; } p { font-size: 14px; color: #3c4043; line-height: 1.4; margin-bottom: 5px; } small { font-size: 12px; color: ${LightText}; display: block; } @media (max-width: 768px) { padding: 12px 15px; p { font-size: 15px; } small { font-size: 12px; } } `;
const ActionButtons = styled.div` display: flex; gap: 8px; margin-top: 8px; `;
const ActionButton = styled.button` padding: 6px 12px; border: none; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; transition: opacity 0.2s; display: flex; align-items: center; background-color: ${(props) => (props.type === 'accept' ? AccentColor : DangerColor)}; color: white; &:hover { opacity: 0.9; } svg { margin-right: 5px; } `;
const NoNotifications = styled.div` padding: 20px; text-align: center; color: ${LightText}; font-size: 14px; `;

const NotificationPopupContent = ({ currentUserId, onClose, isMobile = false }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentUserId) return;
        const notificationsQuery = query(collection(db, "notifications"), where("userId", "==", currentUserId), orderBy("timestamp", "desc"), limit(20));
        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
            const fetchedNotifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), isRead: doc.data().isRead || false }));
            setNotifications(fetchedNotifications); setLoading(false);
        }, (error) => { console.error("Error fetching notifications:", error); setLoading(false); });
        return () => unsubscribe();
    }, [currentUserId]);

    const markAsRead = async (notificationId) => { try { await updateDoc(doc(db, "notifications", notificationId), { isRead: true, }); } catch (error) { console.error("Error marking notification as read:", error); } };
    const getUserName = async (uid) => { const docSnap = await getDoc(doc(db, "profiles", uid)); if (docSnap.exists()) { const data = docSnap.data(); return data.fullName || auth.currentUser.email.split('@')[0]; } return auth.currentUser.email.split('@')[0]; }
    const handleAccept = async (notification) => {
        const { requestId, senderId, id: notificationId } = notification;
        try {
            await updateDoc(doc(db, "connectionRequests", requestId), { status: "accepted", updatedAt: serverTimestamp(), });
            const acceptorName = await getUserName(currentUserId);
            await updateDoc(doc(db, "profiles", currentUserId), { connections: arrayUnion(senderId) });
            await updateDoc(doc(db, "profiles", senderId), { connections: arrayUnion(currentUserId) });
            await addDoc(collection(db, "notifications"), { userId: senderId, message: `${acceptorName} accepted your connection request! You are now connected.`, type: "connection_accepted", timestamp: serverTimestamp(), isRead: false, });
            await deleteDoc(doc(db, "notifications", notificationId)); alert("Connection Accepted!");
        } catch (error) { console.error("Error accepting connection:", error); alert("Failed to accept connection."); }
    };
    const handleReject = async (notification) => {
        const { requestId, senderId, id: notificationId } = notification;
        try {
            await updateDoc(doc(db, "connectionRequests", requestId), { status: "rejected", updatedAt: serverTimestamp(), });
            const rejectorName = await getUserName(currentUserId);
            await addDoc(collection(db, "notifications"), { userId: senderId, message: `${rejectorName} rejected your connection request.`, type: "connection_rejected", timestamp: serverTimestamp(), isRead: false, });
            await deleteDoc(doc(db, "notifications", notificationId)); alert("Connection Rejected.");
        } catch (error) { console.error("Error rejecting connection:", error); alert("Failed to reject connection."); }
    };
    const unreadCount = notifications.filter(n => !n.isRead).length;
    if (loading) return <NoNotifications>Loading...</NoNotifications>;
    return (
        <NotificationPopup isMobile={isMobile}>
            <PopupHeader>Notifications ({unreadCount} New) <MdClose style={{ cursor: 'pointer', color: LightText }} onClick={onClose} /></PopupHeader>
            <NotificationList>
                {notifications.length === 0 ? <NoNotifications>No new notifications.</NoNotifications> : notifications.map(notification => (
                    <NotificationItem key={notification.id} isRead={notification.isRead} onClick={() => !notification.isRead && markAsRead(notification.id)}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <div style={{ minWidth: '20px', paddingTop: '2px' }}>
                                {notification.type === 'connection_request' && <MdMessage size={20} color={PrimaryColor} />}
                                {notification.type === 'like' && <MdThumbUp size={20} color={DangerColor} />} 
                                {notification.type === 'comment' && <MdComment size={20} color={AccentColor} />}
                                {notification.type === 'connection_accepted' && <MdDone size={20} color={AccentColor} />}
                            </div>
                            <div>
                                <p>{notification.message}</p>
                                <small>{notification.timestamp?.toDate().toLocaleTimeString()} - {notification.timestamp?.toDate().toLocaleDateString()}</small>
                                {notification.type === 'connection_request' && (
                                    <ActionButtons>
                                        <ActionButton type="accept" onClick={(e) => { e.stopPropagation(); handleAccept(notification); }}><MdDone /> Accept</ActionButton>
                                        <ActionButton type="reject" onClick={(e) => { e.stopPropagation(); handleReject(notification); }}><MdClear /> Reject</ActionButton>
                                    </ActionButtons>
                                )}
                            </div>
                        </div>
                    </NotificationItem>
                ))}
            </NotificationList>
        </NotificationPopup>
    );
};


// === MAIN APP HEADER ===

const AppHeader = () => {
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [initials, setInitials] = useState("U");
    const [currentUser, setCurrentUser] = useState(null);
    const [currentUserName, setCurrentUserName] = useState(null); 
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showMobileNotifications, setShowMobileNotifications] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    // New state for unread chat count
    const [unreadMessageCount, setUnreadMessageCount] = useState(0); 
    const [showSearchDropdown, setShowSearchDropdown] = useState(false); 

    const searchBarRef = useRef(null);
    const notificationRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const searchIconRef = useRef(null);
    const navigate = useNavigate();
    
    const handleNavigation = (path) => {
        navigate(path);
        setShowMobileMenu(false);
    }
    
    const handleShowNotificationsInMenu = () => {
        if (!currentUser) {
            alert("Please login to view notifications.");
            return;
        }
        setShowMobileNotifications(true);
        setShowMobileMenu(false);
    }
  
    const toggleNotifications = () => {
        if (!currentUser) {
            alert("Please login to view notifications.");
            return;
        }
        setShowNotifications(prev => !prev);
        setShowSearchDropdown(false);
    };
    
    const toggleMobileMenu = () => {
        setShowMobileMenu(prev => !prev);
        setShowSearchDropdown(false);
    }
    
    const toggleSearchDropdown = () => {
        setShowSearchDropdown(prev => {
            const newState = !prev;
            if (newState) {
                setShowNotifications(false);
                setShowMobileMenu(false);
                setTimeout(() => {
                    const inputElement = searchBarRef.current?.querySelector('input');
                    if (inputElement) inputElement.focus();
                }, 10);
            } else {
                setSearchResults([]);
                setSearchQuery('');
            }
            return newState;
        });
    }

    const getSingleConnectionStatus = useCallback(async (currentUserId, targetUserId) => {
        const targetProfileDoc = await getDoc(doc(db, "profiles", targetUserId));
        const isConnected = targetProfileDoc.exists() && targetProfileDoc.data()?.connections?.includes(currentUserId);
        if (isConnected) return 'connected';

        const q = query(
            collection(db, "connectionRequests"),
            or(
                and(where('senderId', '==', currentUserId), where('receiverId', '==', targetUserId)),
                and(where('senderId', '==', targetUserId), where('receiverId', '==', currentUserId))
            ),
            limit(1)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const data = snapshot.docs[0].data();
            if (data.status === 'pending' && data.senderId === currentUserId) return 'requested'; 
            if (data.status === 'rejected' && data.senderId === currentUserId) return 'rejected'; 
            return 'default'; 
        }
        return 'default';
    }, []);

    const handleSearch = async (queryText) => {
        setSearchQuery(queryText);
        const trimmedQuery = queryText.trim();
        if (trimmedQuery.length < 2) {
            setSearchResults([]);
            return;
        }
        const prefixQuery = trimmedQuery.toLowerCase().replace(/\s/g, '').replace(/[^a-z0-9]/g, '');
        if (prefixQuery.length === 0) {
            setSearchResults([]);
            return;
        }
        try {
            const profilesRef = collection(db, 'profiles');
            const q = query(
                profilesRef,
                where('fullNameLower', '>=', prefixQuery),
                where('fullNameLower', '<=', prefixQuery + '\uf8ff'),
                limit(10) 
            );
            const snapshot = await getDocs(q);
            const foundResults = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id, 
                    fullName: data.fullName,
                    profileImageUrl: data.profileImageUrl,
                    profession: data.profession, 
                    subRole: data.subRole, 
                    mainRole: data.mainRole, 
                    studentType: data.studentType, 
                };
            });
            setSearchResults(foundResults);
        } catch (error) {
            console.error("Error searching for users:", error);
            setSearchResults([]);
        }
    };

    const handleProfileClick = async (targetUserId, targetFullName) => {
        const user = auth.currentUser;
        if (!user) {
            alert("Please login to view the profile.");
            setShowSearchDropdown(false); setSearchQuery(''); setSearchResults([]);
            return;
        }
        if (user.uid === targetUserId) {
            const urlName = currentUserName ? encodeURIComponent(currentUserName.trim().replace(/\s+/g, '-')) : user.uid;
            navigate(`/profile/${urlName}`); 
            setShowSearchDropdown(false); setSearchQuery(''); setSearchResults([]);
            return;
        }
        try {
            const status = await getSingleConnectionStatus(user.uid, targetUserId);
            if (status === 'connected') {
                navigate(`/profileview/${targetUserId}`); 
            } else if (status === 'requested') {
                alert(`You have to wait until ${targetFullName} accepts your request.`);
            } else if (status === 'rejected') {
                alert(`You are not allowed to view ${targetFullName}'s profile.`);
            } else { 
                alert(`Connect to view ${targetFullName}'s profile.`);
            }
        } catch (e) {
            console.error("Error handling profile click:", e);
            alert("Failed to check profile status. Please try again.");
        } finally {
            setShowSearchDropdown(false); setSearchQuery(''); setSearchResults([]);
        }
    };

    const handleMyProfileNavigation = async (userName) => {
        const user = auth.currentUser;
        if (!user) {
            navigate("/login");
            return;
        }
        if (userName) {
            const urlName = encodeURIComponent(userName.trim().replace(/\s+/g, '-')); 
            navigate(`/profile/${urlName}`); 
        } else {
            navigate(`/profile/${user.uid}`);
        }
    };
   
    // --- MAIN EFFECT: Auth & Real-time Profile Listener ---
    useEffect(() => {
        let unsubNotifications; 
        let unsubProfile; 
        let unsubChats; // New unsubscribe variable for chats

        const unsubAuth = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            
            // Clean up previous listeners
            if (unsubNotifications) unsubNotifications();
            if (unsubProfile) unsubProfile();
            if (unsubChats) unsubChats(); // Clean up chat listener

            if (user) {
                // 1. SYNC PROFILE (PREVENTS OVERWRITING)
                try {
                    const userRef = doc(db, "profiles", user.uid);
                    
                    // Check if profile ALREADY exists
                    const docSnap = await getDoc(userRef);

                    if (!docSnap.exists()) {
                        // Create NEW profile (Only runs ONCE for new users)
                        await setDoc(userRef, {
                            id: user.uid,
                            lastSeen: serverTimestamp(),
                            fullName: user.displayName || "User",
                            email: user.email,
                            profileImageUrl: user.photoURL,
                            fullNameLower: (user.displayName || "user").toLowerCase().replace(/\s/g, '')
                        });
                    } else {
                        // Profile EXISTS: Just update 'lastSeen', DO NOT touch 'fullName'
                        await updateDoc(userRef, {
                            lastSeen: serverTimestamp()
                        });
                    }

                } catch (error) {
                    console.error("Error syncing profile:", error);
                }

                // 2. REAL-TIME PROFILE LISTENER
                unsubProfile = onSnapshot(doc(db, "profiles", user.uid), (docSnap) => {
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        
                        // Use DB data first
                        setProfileImageUrl(data.profileImageUrl || user.photoURL);
                        setInitials(data.fullName?.[0]?.toUpperCase() || "U");
                        setCurrentUserName(data.fullName);
                    }
                });

                // 3. Notifications Listener
                const notificationsQuery = query(
                    collection(db, "notifications"),
                    where("userId", "==", user.uid),
                    where("isRead", "==", false)
                );
                
                unsubNotifications = onSnapshot(notificationsQuery, (snapshot) => {
                    setUnreadCount(snapshot.size); 
                }, (error) => {
                    console.error("Error fetching unread count:", error);
                });
                
                // 4. CHAT/MESSAGES Listener (Finds chats where unreadCount[user.uid] > 0)
                const chatsQuery = query(
                    collection(db, "chats"),
                    or(where("user1Id", "==", user.uid), where("user2Id", "==", user.uid))
                );

                unsubChats = onSnapshot(chatsQuery, (snapshot) => {
                    let totalUnreadMessages = 0;
                    snapshot.forEach((chatDoc) => {
                        const chatData = chatDoc.data();
                        // Assuming the structure has a map named 'unreadCount' 
                        // where keys are user IDs and values are unread message counts.
                        const unreadCountForCurrentUser = chatData.unreadCount?.[user.uid] || 0;
                        totalUnreadMessages += unreadCountForCurrentUser;
                    });
                    setUnreadMessageCount(totalUnreadMessages);
                }, (error) => {
                    console.error("Error fetching unread chat count:", error);
                });


            } else {
                setProfileImageUrl(null);
                setInitials("U");
                setUnreadCount(0); 
                setUnreadMessageCount(0); // Reset message count on logout
                setCurrentUserName(null);
            }
        });

        const handleClickOutside = (event) => {
            if (showSearchDropdown && searchBarRef.current && !searchBarRef.current.contains(event.target) && searchIconRef.current && !searchIconRef.current.contains(event.target)) {
                setShowSearchDropdown(false);
                setSearchResults([]);
                setSearchQuery('');
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
                const menuButton = document.getElementById('menu-toggle-button');
                if (menuButton && menuButton.contains(event.target)) return; 
                setShowMobileMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            unsubAuth();
            if (unsubNotifications) unsubNotifications(); 
            if (unsubProfile) unsubProfile(); 
            if (unsubChats) unsubChats(); // Clean up chat listener
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSearchDropdown]); 

    const handleLogout = async () => {
        if (currentUser) {
            try {
                const userRef = doc(db, "profiles", currentUser.uid); 
                await updateDoc(userRef, {
                    status: "logout",
                    lastSeen: serverTimestamp()
                });
            } catch (error) {
                console.error("Error updating logout status:", error);
            }
        }
        await signOut(auth);
        navigate("/login");
    };

    return (
        <HeaderContainer>
            <MenuButton onClick={toggleMobileMenu} id="menu-toggle-button" isSearchOpen={showSearchDropdown}>
                {showMobileMenu ? <MdClose /> : <MdMenu />}
            </MenuButton>
          
            <Logo onClick={() => navigate("/home")} isSearchOpen={showSearchDropdown}>
                <img src={logo} alt="EduConnect Logo" />
                WeForSmart[Test]
            </Logo>

            <SearchBar ref={searchBarRef} isSearchOpen={showSearchDropdown}>
                <SearchInputContainer>
                    <SearchIcon />
                    <SearchInput
                        placeholder="Search for users, posts..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { } }}
                    />
                    <SearchCloseIcon onClick={() => setShowSearchDropdown(false)} />
                </SearchInputContainer>

                {searchResults.length > 0 && (
                    <SearchResultsDropdown>
                        {searchResults.map(result => (
                            <SearchResultItem
                                key={result.id} 
                                onClick={() => handleProfileClick(result.id, result.fullName)}
                            >
                                <ProfileImageSmall
                                    src={result.profileImageUrl || `https://placehold.co/30x30?text=${result.fullName?.charAt(0) || 'U'}`}
                                    alt={result.fullName}
                                    referrerPolicy="no-referrer"
                                />
                                <ResultDetails>
                                    <span>{result.fullName}</span>
                                    <small>
                                        {result.mainRole === 'educator' 
                                            ? (result.subRole || 'Educator Role')
                                            : result.mainRole === 'Student'
                                                ? (result.studentType || 'Student Type')
                                                : (result.subRole || 'User Role') 
                                        }
                                    </small> 
                                </ResultDetails>
                            </SearchResultItem>
                        ))}
                    </SearchResultsDropdown>
                )}
            </SearchBar>

            <NavLinks isSearchOpen={showSearchDropdown}>
                <NavItem onClick={() => handleNavigation("/Home")}>
                    <MdHome /> <span>Home</span>
                </NavItem>
                <NavItem onClick={() => handleNavigation("/EduCorners")}>
                    <MdSchool /> <span>Educators</span>
                </NavItem>
                <NavItem onClick={() => handleNavigation("/StuCorners")}>
                    <MdBook /> <span>Learners </span>
                </NavItem>
                
                <SearchIconButton
                    ref={searchIconRef}
                    title="Search"
                    onClick={toggleSearchDropdown}
                    isSearchOpen={showSearchDropdown}
                >
                    <MdSearch />
                </SearchIconButton>

                {/* Updated Messaging Icon with Message Badge */}
                <MessageContainer>
                    <MobileHiddenIconButton title="Messaging" onClick={() => handleNavigation("/messages")}>
                        <MdMessage />
                    </MobileHiddenIconButton>
                    {unreadMessageCount > 0 && <NotificationBadge>{unreadMessageCount > 9 ? '9+' : unreadMessageCount}</NotificationBadge>}
                </MessageContainer>

                <NotificationContainer ref={notificationRef}>
                    <IconButton title="Notifications" onClick={toggleNotifications}>
                        <MdNotifications />
                    </IconButton>
                    {unreadCount > 0 && <NotificationBadge>{unreadCount > 9 ? '9+' : unreadCount}</NotificationBadge>}
                    {showNotifications && currentUser && (
                        <NotificationPopupContent
                            currentUserId={currentUser.uid}
                            onClose={() => setShowNotifications(false)}
                            isMobile={false}
                        />
                    )}
                </NotificationContainer>

                <MobileHiddenIconButton title="Logout" onClick={handleLogout}>
                    <MdLogout />
                </MobileHiddenIconButton>

                <ProfileIcon 
                    onClick={() => handleMyProfileNavigation(currentUserName)} 
                    isSearchOpen={showSearchDropdown}
                >
                    {profileImageUrl ? (
                        <img 
                            src={profileImageUrl} 
                            alt="Profile" 
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        initials
                    )}
                </ProfileIcon>
            </NavLinks>
            
            <MobileMenu isOpen={showMobileMenu} ref={mobileMenuRef}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 15px 12px', borderBottom: `1px solid ${BorderColor}` }}>
                    <Logo style={{ margin: 0, order: 'unset', fontSize: '18px' }} onClick={() => handleNavigation("/home")}>
                        <img src={logo} alt="EduConnect Logo" style={{height: '25px'}} />
                        WeForSmart[Test]
                    </Logo>
                    <MdClose onClick={() => setShowMobileMenu(false)} style={{ cursor: 'pointer', fontSize: '24px', color: LightText }}/>
                </div>
                
                <MobileMenuItem onClick={() => handleNavigation("/Home")}> <MdHome /> Home </MobileMenuItem>
                <MobileMenuItem onClick={() => handleNavigation("/EduCorners")}> <MdSchool /> Educators </MobileMenuItem>
                <MobileMenuItem onClick={() => handleNavigation("/StuCorners")}> <MdBook /> Learners </MobileMenuItem>
                
                <hr style={{border: `1px solid ${BorderColor}`, margin: '10px 0'}}/> 
                
                <MobileMenuItem onClick={handleShowNotificationsInMenu}>
                    <MdNotifications /> Notifications 
                    {unreadCount > 0 && (
                        <NotificationBadge style={{position: 'relative', top: '-2px', marginLeft: '10px'}}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </NotificationBadge>
                    )}
                </MobileMenuItem>
                
                {/* Updated Mobile Messages Item with Message Badge */}
                <MobileMenuItem onClick={() => handleNavigation("/messages")}> 
                    <MdMessage /> Messages 
                    {unreadMessageCount > 0 && (
                        <NotificationBadge style={{position: 'relative', top: '-2px', marginLeft: '10px'}}>
                            {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                        </NotificationBadge>
                    )}
                </MobileMenuItem>
                
                {currentUser && (
                    <MobileMenuItem onClick={handleLogout}> <MdLogout /> Logout </MobileMenuItem>
                )}
            </MobileMenu>
            
            {showMobileNotifications && currentUser && (
                <NotificationPopupContent
                    currentUserId={currentUser.uid}
                    onClose={() => setShowMobileNotifications(false)}
                    isMobile={true}
                />
            )}
        </HeaderContainer>
    );
};

export default AppHeader;